import { useEffect, useState, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { withRouter } from '../utils'
import axios from 'axios'
import moment from 'moment'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Area, AreaChart, ComposedChart } from 'recharts'
import '../styles/dailyreports.css'
import { trackPageView, trackAction } from '../tracker'
import { UpgradePopup } from '../components/Billing'

const FALLBACK_COUNTRIES = [
  "Saudi Arabia", "UAE", "Kuwait", "Bahrain", "Qatar", "Oman",
  "Syria", "Lebanon", "Palestine", "Jordan", "Iraq", "Yemen", "Egypt",
  "Turkey", "Iran", "Afghanistan", "Pakistan", "Libya", "Tunisia",
  "Algeria", "Morocco", "Sudan", "Somalia", "Israel",
  "Ukraine", "Russia", "South Sudan", "Myanmar",
]

const GCC_COUNTRIES = ["Saudi Arabia", "UAE", "Kuwait", "Bahrain", "Qatar", "Oman"]

const QUICK_RANGES = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '6m', days: 180 },
  { label: '1y', days: 365 },
]


const VIOLENCE_ICONS = {
  'Missile Attack': '↑',
  'Drone Attack': '≡',
  'Bombing': '◉',
  'Airstrike': '⌖',
  'Shelling / Artillery': '▓',
  'Suicide Attack': '⚠',
  'Armed Assault': '†',
  'Ambush': '▷',
  'Car Bombing (VBIED)': '▣',
  'IED / Roadside Bomb': '◆',
  'Assassination / Targeted Killing': '⌖',
  'Kidnapping / Hostage Taking': '☷',
  'Maritime Attack': '⚓',
  'Cyber Attack': '⌘',
  'Chemical / Biological Attack': '☣',
  'Insurgency': '⚔',
  'Terrorist Activity': '☢',
  'Repression': '█',
  'Atrocities': '✘',
  'Ethnic / Sectarian Violence': '❀',
  'Foreign Military Engagement': '★',
  'Border Clash': '│',
  'Naval Confrontation': '⚓',
  'Siege / Blockade': '○',
  'Sabotage': '✂',
  'Riot / Civil Unrest': '░',
  'Other': '•',
}

function toInputDate(date) {
  return date.toISOString().split('T')[0]
}

function DailyReports({ navigate, embedded = false, headerCountries, headerDateFrom, headerDateTo, trial = false }) {
  const apiHost = process.env.REACT_APP_APIHOST || 'http://localhost:3033'
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCountries, setSelectedCountries] = useState(GCC_COUNTRIES)
  const [expandedId, setExpandedId] = useState(null)
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 })
  const [countryInput, setCountryInput] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [allCountries, setAllCountries] = useState(FALLBACK_COUNTRIES)
  const [activeTab, setActiveTab] = useState('list')
  const [selectedMapEvent, setSelectedMapEvent] = useState(null)
  const [reports, setReports] = useState([])
  const [expandedReport, setExpandedReport] = useState('first')
  const [arsenalData, setArsenalData] = useState(null)
  const [arsenalLoading, setArsenalLoading] = useState(false)
  const [arsenalFilter, setArsenalFilter] = useState('all')
  const [airspaceData, setAirspaceData] = useState(null)
  const [airspaceLoading, setAirspaceLoading] = useState(false)
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryExpanded, setSummaryExpanded] = useState(false)
  const [dayMode, setDayMode] = useState(false)

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 14); return toInputDate(d)
  })
  const [dateTo, setDateTo] = useState(() => toInputDate(new Date()))
  const [activeQuick, setActiveQuick] = useState(14)

  // Sync from header when embedded
  useEffect(() => {
    if (embedded && headerCountries && headerCountries.length > 0) setSelectedCountries(headerCountries)
  }, [embedded, headerCountries])
  useEffect(() => {
    if (embedded && headerDateFrom) { setDateFrom(headerDateFrom); setActiveQuick(null) }
  }, [embedded, headerDateFrom])
  useEffect(() => {
    if (embedded && headerDateTo) { setDateTo(headerDateTo); setActiveQuick(null) }
  }, [embedded, headerDateTo])

  useEffect(() => { axios.get(apiHost + '/events/meta/countries').then(r => { if (r.data.status && r.data.data.length > 0) setAllCountries(r.data.data) }).catch(() => {}) }, [apiHost])

  useEffect(() => { document.documentElement.classList.add("dr-active"); document.body.className = "dailyreports-page"; return () => { document.documentElement.classList.remove("dr-active"); document.body.className = ""; }; }, [])

  useEffect(() => {
    if (dayMode) document.body.classList.add('dr-day')
    else document.body.classList.remove('dr-day')
  }, [dayMode])

  const fetchSummary = async (countries, from, to) => {
    try {
      setSummaryLoading(true)
      const params = new URLSearchParams()
      if (countries && countries.length > 0) params.append('countries', countries.join(','))
      if (from) params.append('dateFrom', from)
      if (to) params.append('dateTo', to)
      const res = await axios.get(apiHost + '/summary?' + params.toString())
      if (res.data.status && res.data.data) { setSummary(res.data.data) }
    } catch (err) { console.error('Summary error:', err) }
    finally { setSummaryLoading(false) }
  }

  useEffect(() => { trackPageView("/daily-reports"); fetchReports() }, [])
  useEffect(() => { fetchEvents() }, [dateFrom, dateTo])

  const fetchAirspace = async (countries) => {
    try {
      setAirspaceLoading(true)
      const params = new URLSearchParams()
      if (countries && countries.length > 0) params.append('countries', countries.join(','))
      const res = await axios.get(apiHost + '/airspace?' + params.toString())
      if (res.data.status) setAirspaceData(res.data.data)
    } catch(e) { console.error(e) }
    finally { setAirspaceLoading(false) }
  }

  const fetchArsenal = async (countries, from, to) => {
    try {
      setArsenalLoading(true)
      const params = new URLSearchParams()
      if (countries && countries.length > 0) params.append('countries', countries.join(','))
      if (from) params.append('dateFrom', from)
      if (to) params.append('dateTo', to)
      const res = await axios.get(apiHost + '/arsenal?' + params.toString())
      if (res.data.status) setArsenalData(res.data.data)
    } catch(e) { console.error(e) }
    finally { setArsenalLoading(false) }
  }

  const fetchReports = async () => {
    try {
      const res = await axios.get(apiHost + '/reports?status=Published')
      if (res.data.status) setReports(res.data.data)
    } catch (err) { console.error(err) }
  }

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${apiHost}/events?limit=5000&status=Published&startDate=${dateFrom}&endDate=${dateTo}`)
      if (res.data.status && res.data.data) setEvents(res.data.data)
    } catch (err) { console.error('Failed to fetch events:', err) }
    finally { setLoading(false) }
  }

  const handleQuickRange = (days) => {
    const to = new Date(); const from = new Date(); from.setDate(from.getDate() - days)
    setDateFrom(toInputDate(from)); setDateTo(toInputDate(to)); setActiveQuick(days)
  }
  const handleDateFromChange = (e) => { setDateFrom(e.target.value); setActiveQuick(null) }
  const handleDateToChange = (e) => { setDateTo(e.target.value); setActiveQuick(null) }

  const filteredEvents = useMemo(() => {
    let result = events
    if (selectedCountries.length > 0) result = result.filter(e => selectedCountries.includes(e.country))
    if (dateFrom) { const fd = new Date(dateFrom); fd.setHours(0,0,0,0); result = result.filter(e => e.startDate && new Date(e.startDate) >= fd) }
    if (dateTo) { const td = new Date(dateTo); td.setHours(23,59,59,999); result = result.filter(e => e.startDate && new Date(e.startDate) <= td) }
    return result
  }, [events, selectedCountries, dateFrom, dateTo])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filteredEvents.length > 0) fetchSummary(selectedCountries, dateFrom, dateTo)
    }, 4000)
    return () => clearTimeout(timer)
  }, [filteredEvents.length, selectedCountries.join(','), dateFrom, dateTo])

  const matchingReports = useMemo(() => {
    if (selectedCountries.length === 0) return reports
    return reports.filter(r => (r.countries || []).some(c => selectedCountries.includes(c)))
  }, [reports, selectedCountries])

  // Technical reports from events with technicalReportUrl
  const technicalReports = useMemo(() => {
    return filteredEvents.filter(e => typeof e.technicalReportUrl === "string" && e.technicalReportUrl.indexOf("http") === 0).map(e => ({
      _id: 'tech_' + e._id,
      type: 'technical',
      title: e.conflictName || 'Satellite analysis',
      date: e.startDate,
      countries: [e.country].filter(Boolean),
      categories: ['Technical'],
      url: e.technicalReportUrl,
      description: e.description,
      country: e.country,
      typeOfViolence: e.typeOfViolence,
    }))
  }, [filteredEvents])

  const [reportSubTab, setReportSubTab] = useState('intel')

  const allReportsCount = matchingReports.length + technicalReports.length

  const latestReport = matchingReports.length > 0 ? matchingReports[0] : null

  const sortedEvents = useMemo(() => [...filteredEvents].sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0)), [filteredEvents])

  // Chart data
  const chartData = useMemo(() => {
    const map = {}
    filteredEvents.forEach(e => {
      if (!e.startDate) return
      const day = new Date(e.startDate).toISOString().split('T')[0]
      if (!map[day]) map[day] = { date: day, events: 0, casualties: 0 }
      map[day].events += 1
      map[day].casualties += (e.sideADeath || 0) + (e.sideBDeath || 0) + (e.deathCivilians || 0) + (e.deathUnknown || 0)
    })
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date))
  }, [filteredEvents])

  // Map data
  const mapEvents = useMemo(() => filteredEvents.filter(e => e.latitude && e.longitude), [filteredEvents])

  const mapSrcDoc = useMemo(() => {
    if (mapEvents.length === 0) return ''
    const evData = mapEvents.flatMap(e => {
      const cas = (e.sideADeath||0)+(e.sideBDeath||0)+(e.deathCivilians||0)+(e.deathUnknown||0)
      const base = {
        cas,
        title: (e.conflictName||'').replace(/'/g,"&#39;"),
        country: e.country||'',
        date: e.startDate ? e.startDate.split('T')[0] : '',
        type: (e.typeOfViolence||'').replace(/'/g,"&#39;"),
        ts: e.startDate ? new Date(e.startDate).getTime() : 0,
      }
      if (e.markers && e.markers.length > 0) {
        return e.markers.filter(mk => mk && mk.lat && mk.lng).map(mk => ({
          ...base, lat: parseFloat(mk.lat), lng: parseFloat(mk.lng), label: (mk.label||'').replace(/'/g,"&#39;")
        }))
      }
      if (e.latitude && e.longitude) return [{ ...base, lat: parseFloat(e.latitude), lng: parseFloat(e.longitude), label: '' }]
      return []
    })
    const lats = evData.map(e => e.lat)
    const lngs = evData.map(e => e.lng)
    const bounds = `[[${Math.min(...lats)-1},${Math.min(...lngs)-1}],[${Math.max(...lats)+1},${Math.max(...lngs)+1}]]`
    return `<!DOCTYPE html><html><head>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
<script src="https://unpkg.com/leaflet.heat@0.2.0/dist/leaflet-heat.js"><\/script>
<style>
html,body{margin:0;height:100%;width:100%;overflow:hidden;font-family:Inter,sans-serif}
#m{width:100%;height:calc(100% - 44px)}

@keyframes pulse{0%{opacity:0.9}50%{opacity:0.4}100%{opacity:0.9}}
.pulse-marker{animation:pulse 3s ease-in-out infinite}
.leaflet-tile{filter:brightness(1.4) contrast(1.3)}.leaflet-control-zoom a{background:#1a1a1a;color:#C29717;border-color:#333}
.leaflet-control-zoom a:hover{background:#333}
.leaflet-popup-content-wrapper{background:#1e1e1e;color:#ccc;border-radius:2px;border:1px solid rgba(255,255,255,0.1)}
.leaflet-popup-tip{background:#1e1e1e}
.leaflet-popup-content{font-size:12px}
#ctrl{height:44px;background:#111;display:flex;align-items:center;padding:0 12px;gap:12px;border-top:1px solid rgba(255,255,255,0.06)}
#ctrl button{background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.12);color:#ccc;padding:4px 12px;font-size:11px;cursor:pointer;border-radius:2px;font-family:Inter,sans-serif}
#ctrl button:hover{border-color:#C29717;color:#C29717}
#ctrl button.active{background:rgba(194,151,23,0.15);border-color:#C29717;color:#C29717}
#slider{flex:1;-webkit-appearance:none;height:3px;background:#333;outline:none;border-radius:2px}
#slider::-webkit-slider-thumb{-webkit-appearance:none;width:12px;height:12px;background:#C29717;border-radius:50%;cursor:pointer}
#dateLabel{color:#C29717;font-size:11px;min-width:80px;text-align:center;letter-spacing:0.5px}
#playBtn{font-size:14px;width:28px;height:28px;display:flex;align-items:center;justify-content:center;padding:0}
</style></head><body>
<div id="m"></div>
<div id="ctrl">
  <button id="dotsBtn" class="active" onclick="showDots()">Markers</button>
  <button id="heatBtn" onclick="showHeat()">Heatmap</button>
  <span style="color:#333;margin:0 4px">|</span>
  <button id="playBtn" onclick="togglePlay()">\u25B6</button>
  <input type="range" id="slider" min="0" max="100" value="100" oninput="onSlide(this.value)"/>
  <span id="dateLabel">All events</span>
</div>
<script>
var events=${JSON.stringify(evData)};
var m=L.map("m",{zoomControl:true,attributionControl:false}).fitBounds(${bounds});
L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}").addTo(m);L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}").addTo(m);

var markersLayer=L.layerGroup().addTo(m);
var heatLayer=null;
var mode="dots";
var playing=false;
var playTimer=null;

// Sort events by date
events.sort(function(a,b){return a.ts-b.ts});
var minTs=events[0]?events[0].ts:0;
var maxTs=events[events.length-1]?events[events.length-1].ts:0;
var currentMax=maxTs;

function getColor(cas){return cas>10?"#e55":cas>0?"#e88a2a":"#C29717"}
function getRadius(cas){return Math.max(5,Math.min(Math.sqrt(cas)*4+5,35))}

function renderDots(evts){
  markersLayer.clearLayers();
  evts.forEach(function(e){
    L.circleMarker([e.lat,e.lng],{radius:getRadius(e.cas),color:getColor(e.cas),weight:1.5,fillColor:getColor(e.cas),fillOpacity:0.7,className:"pulse-marker"})
    .bindPopup("<b>"+e.title+"</b><br>"+e.country+"<br>"+e.cas+" casualties<br><small>"+e.date+"</small>")
    .addTo(markersLayer);
  });
}

function renderHeat(evts){
  if(heatLayer){m.removeLayer(heatLayer);heatLayer=null}
  var pts=evts.map(function(e){return[e.lat,e.lng,Math.max(e.cas,1)]});
  heatLayer=L.heatLayer(pts,{radius:25,blur:20,maxZoom:10,gradient:{0.2:"#C29717",0.5:"#e88a2a",0.8:"#e55",1:"#ff0000"}}).addTo(m);
}

function filterByTime(maxTime){
  currentMax=maxTime;
  var evts=events.filter(function(e){return e.ts<=maxTime});
  if(mode==="dots")renderDots(evts);
  else renderHeat(evts);
  // Update label
  if(maxTime>=maxTs){document.getElementById("dateLabel").innerText="All events"}
  else{var d=new Date(maxTime);document.getElementById("dateLabel").innerText=d.toLocaleDateString("en",{day:"numeric",month:"short",year:"numeric"})}
}

function showDots(){
  mode="dots";
  if(heatLayer){m.removeLayer(heatLayer);heatLayer=null}
  markersLayer.addTo(m);
  filterByTime(currentMax);
  document.getElementById("dotsBtn").classList.add("active");
  document.getElementById("heatBtn").classList.remove("active");
}

function showHeat(){
  mode="heat";
  markersLayer.clearLayers();
  filterByTime(currentMax);
  document.getElementById("heatBtn").classList.add("active");
  document.getElementById("dotsBtn").classList.remove("active");
}

function onSlide(val){
  var t=minTs+(maxTs-minTs)*(val/100);
  filterByTime(t);
}

function togglePlay(){
  if(playing){
    playing=false;clearInterval(playTimer);
    document.getElementById("playBtn").innerText="\u25B6";
  }else{
    playing=true;
    document.getElementById("playBtn").innerText="\u23F8";
    var slider=document.getElementById("slider");
    var v=parseInt(slider.value);
    if(v>=100)v=0;
    playTimer=setInterval(function(){
      v+=1;if(v>100){v=100;playing=false;clearInterval(playTimer);document.getElementById("playBtn").innerText="\u25B6"}
      slider.value=v;onSlide(v);
    },200);
  }
}

// Initial render
renderDots(events);
<\/script></body></html>`
  }, [mapEvents])

  const toggleCountry = (country) => setSelectedCountries(prev => prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country])
  const availableCountries = allCountries.filter(c => c.toLowerCase().includes(countryInput.toLowerCase()) && !selectedCountries.includes(c))
  const totalCasualties = (e) => (e.sideADeath || 0) + (e.sideBDeath || 0) + (e.deathCivilians || 0) + (e.deathUnknown || 0)
  const openLightbox = (imgs, idx) => setLightbox({ open: true, images: imgs, index: idx })
  const closeLightbox = () => setLightbox({ open: false, images: [], index: 0 })
  const prevImage = () => setLightbox(prev => ({ ...prev, index: (prev.index - 1 + prev.images.length) % prev.images.length }))
  const nextImage = () => setLightbox(prev => ({ ...prev, index: (prev.index + 1) % prev.images.length }))
  const timeAgo = (dateStr) => {
    if (!dateStr) return ''
    const diff = Date.now() - new Date(dateStr).getTime()
    if (diff < -3600000) return '' // only hide if more than 1h in future (timezone tolerance)
    if (diff < 0) return 'just now'
    const mins = Math.floor(diff / 60000)
    const hrs = Math.floor(mins / 60)
    const days = Math.floor(hrs / 24)
    if (days > 0) return days + 'd ' + (hrs % 24) + 'h ago'
    if (hrs > 0) return hrs + 'h ' + (mins % 60) + 'min ago'
    if (mins === 0) return 'just now'
    return mins + 'min ago'
  }

  const buildEventMapSrc = (event) => {
    const mks = event.markers && event.markers.length > 0 ? event.markers : (event.latitude ? [{label:'',lat:parseFloat(event.latitude),lng:parseFloat(event.longitude)}] : [])
    if (mks.length === 0) return ''
    const center = mks[0]
    const zoom = mks.length > 1 ? 6 : 10
    const circles = mks.map(mk => 'L.circleMarker(['+mk.lat+','+mk.lng+'],{radius:6,color:"#C29717",weight:2,fillColor:"#C29717",fillOpacity:0.9}).addTo(m)' + (mk.label ? '.bindTooltip("'+mk.label.replace(/"/g,"'")+'",{permanent:true,direction:"top",offset:[0,-8],className:"mk-tip"})' : '') + ';').join('')
    return '<!DOCTYPE html><html><head><link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/><script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script><style>body{margin:0}#m{width:100%;height:100vh}.leaflet-tile{filter:brightness(1.4) contrast(1.3)}.mk-tip{background:rgba(26,26,26,0.9);border:1px solid rgba(194,151,23,0.3);color:#C29717;font-size:9px;padding:1px 4px;border-radius:2px;font-family:Inter,sans-serif}</style></head><body><div id="m"></div><script>var m=L.map("m",{zoomControl:false,attributionControl:false,scrollWheelZoom:false,dragging:false}).setView(['+center.lat+','+center.lng+'],'+zoom+');L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}").addTo(m);L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}").addTo(m);'+circles+'<\/script></body></html>'
  }

  const _buildTs2 = Date.now(); const formatContent = (s) => {
    if (!s) return "";
    var t = s;
    t = t.split("color:#000000").join("color:#ddd");
    t = t.split("color: #000000").join("color: #ddd");
    t = t.split("color:#000;").join("color:#ddd;");
    t = t.split("color: #000;").join("color: #ddd;");
    t = t.split("color:black").join("color:#ddd");
    t = t.split("color: black").join("color: #ddd");
    t = t.split("color:#111").join("color:#e6e6e6");
    t = t.split("color: #111").join("color: #e6e6e6");
    t = t.split("color:#222").join("color:#ddd");
    t = t.split("color: #222").join("color: #ddd");
    t = t.split("color:#333").join("color:#ccc");
    t = t.split("color: #333").join("color: #ccc");
    t = t.split("color:rgb(0,0,0)").join("color:#ddd");
    t = t.split("color:rgb(0, 0, 0)").join("color:#ddd");
    t = t.split("\n").join("<br/>");
    t = t.split(String.fromCharCode(10)).join("<br/>");
    while (t.indexOf("<br/><br/><br/>") > -1) { t = t.split("<br/><br/><br/>").join("<br/><br/>"); }
    return t;
  }

  

  const truncate = (text, len) => !text ? '' : text.length > len ? text.substring(0, len) + '...' : text

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null
    return (
      <div style={{ background: '#1e1e1e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px', padding: '8px 12px', fontSize: '11px', color: '#ccc' }}>
        <div style={{ color: '#fff', marginBottom: '4px' }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color }}>{p.name}: {p.value}</div>
        ))}
      </div>
    )
  }

  // ---- Free-trial gating ----
  const [showPay, setShowPay] = useState(false)
  const TRIAL_VISIBLE_REPORTS = 2
  const gatedTab = trial && ['forecast', 'analytics', 'arsenal', 'airspace'].includes(activeTab)
  const goUpgrade = () => setShowPay(true)
  const TrialCta = ({ title, sub }) => (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center', padding: 24, background: 'linear-gradient(180deg, rgba(26,26,26,0.15) 0%, rgba(26,26,26,0.62) 100%)', zIndex: 5 }}>
      <span style={{ fontSize: 10, letterSpacing: 2.5, textTransform: 'uppercase', color: '#C29717', fontFamily: 'Inter,sans-serif' }}>Free trial</span>
      <div style={{ fontSize: 16, color: '#fff', fontWeight: 600, maxWidth: 380, lineHeight: 1.4 }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: '#9aa0a8', maxWidth: 360, lineHeight: 1.6 }}>{sub}</div>}
      <button onClick={goUpgrade} style={{ marginTop: 6, background: '#C29717', color: '#1a1a1a', border: 'none', borderRadius: 3, padding: '10px 24px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, cursor: 'pointer' }}>Upgrade to unlock &rarr;</button>
    </div>
  )

  return (
    <>
      {showPay && <UpgradePopup onClose={() => setShowPay(false)} />}

      {!embedded && (
      <header className="header-login" id="header">
        <div className="auto__container">
          <div className="header__inner">
            <a href="/" className="header__inner-logo"><img src="icons/logo.webp" alt="logo" /></a>
            <div className="header__inner-side">
              <span className="dr-mode-toggle" onClick={() => setDayMode(!dayMode)} title={dayMode ? 'Switch to night' : 'Switch to day'}>
                {dayMode ? '☽' : '☀'}
              </span>
              <Link to="/login" className="header__inner-link">LOGIN</Link>
            </div>
          </div>
        </div>
      </header>
      )}

      <main className="main sm" style={embedded ? { minHeight: 'calc(100vh - 42px)', background: '#1a1a1a' } : undefined}>
        {!embedded && <div className="main__bg"><img src="icons/bg-4.webp" alt="bg" /></div>}

        <section className="dr-section" style={embedded ? { paddingTop: '20px' } : undefined}>
          <div className="auto__container">
            <div className="dr-content">
              {!embedded && <div className="dr-title">
                <h1>Daily Reports</h1>
                <p className="dr-subtitle">Real-time intelligence feed on conflict events and security incidents (free-access version)</p>
              </div>}

              {/* Country filter — hidden when embedded (controlled by header) */}
              <div className="dr-filters" style={embedded ? {display:'none'} : undefined}>
                <div className="dr-filters-label">Monitoring:</div>
                <div className="dr-chips">
                  {selectedCountries.map(c => (
                    <div className="dr-chip" key={c} onClick={() => toggleCountry(c)}><span>{c}</span><span className="dr-chip-x">&times;</span></div>
                  ))}
                  <div className="dr-add-country">
                    <input type="text" placeholder="+ Add country" value={countryInput}
                      onChange={e => { setCountryInput(e.target.value); setShowDropdown(true) }}
                      onFocus={() => setShowDropdown(true)}
                      onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                      className="dr-country-input" />
                    {showDropdown && availableCountries.length > 0 && (
                      <div className="dr-dropdown">
                        {availableCountries.map(c => (
                          <div key={c} className="dr-dropdown-item" onMouseDown={() => { toggleCountry(c); setCountryInput(''); setShowDropdown(false) }}>{c}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Date filter — hidden when embedded (controlled by header) */}
              <div className="dr-date-filters" style={embedded ? {display:'none'} : undefined}>
                <div className="dr-filters-label">Period:</div>
                <div className="dr-date-controls">
                  <div className="dr-quick-ranges">
                    {QUICK_RANGES.map(r => (
                      <button key={r.days} className={`dr-quick-btn ${activeQuick === r.days ? 'active' : ''}`} onClick={() => handleQuickRange(r.days)}>{r.label}</button>
                    ))}
                  </div>
                  <div className="dr-date-inputs">
                    <div className="dr-date-field"><label className="dr-date-label">From</label><input type="date" value={dateFrom} onChange={handleDateFromChange} className="dr-date-input" /></div>
                    <span className="dr-date-sep">—</span>
                    <div className="dr-date-field"><label className="dr-date-label">To</label><input type="date" value={dateTo} onChange={handleDateToChange} className="dr-date-input" /></div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="dr-tabs">
                <button className={`dr-tab ${activeTab === 'list' ? 'active' : ''}`} onClick={() => { setActiveTab('list'); trackAction('tab_click','events') }}>Events</button>
                <button className={`dr-tab ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => { setActiveTab('reports'); trackAction('tab_click','reports') }}>Reports{allReportsCount > 0 ? ` (${allReportsCount})` : ''}</button>
                <button className={`dr-tab ${activeTab === 'forecast' ? 'active' : ''}`} onClick={() => { setActiveTab('forecast'); trackAction('tab_click','forecast') }}>Forecast</button>
                <button className={`dr-tab ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => { setActiveTab('analytics'); trackAction('tab_click','analytics') }}>Analytics</button>
                <button className={`dr-tab ${activeTab === 'arsenal' ? 'active' : ''}`} onClick={() => { setActiveTab('arsenal'); trackAction('tab_click','arsenal'); fetchArsenal(selectedCountries, dateFrom, dateTo) }}>Arsenal</button>
                <button className={`dr-tab ${activeTab === 'airspace' ? 'active' : ''}`} onClick={() => { setActiveTab('airspace'); trackAction('tab_click','airspace'); fetchAirspace(selectedCountries) }}>Airspace</button>
                <span className="dr-consolidated-link" onClick={() => {
                  const params = new URLSearchParams();
                  if (selectedCountries.length > 0) params.append('countries', selectedCountries.join(','));
                  if (dateFrom) params.append('dateFrom', dateFrom);
                  if (dateTo) params.append('dateTo', dateTo);
                  window.open((process.env.REACT_APP_APIHOST || 'http://localhost:3033') + '/daily-report-view?' + params.toString(), '_blank');
                }}>Consolidated report of the day &#8681;</span>
                <span className="dr-tab-count">{sortedEvents.length} event{sortedEvents.length !== 1 ? 's' : ''}</span>
              </div>

              {/* trial-gated panels wrapper — gated tabs render behind a blurred upgrade overlay */}
              <div style={{ position: 'relative' }}>
              {gatedTab && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 4, backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}>
                  <TrialCta title="This intelligence module is part of the full platform" sub="Forecasts, analytics, arsenal tracking and airspace monitoring are available on paid plans. Your trial includes live events and a preview of the daily reports." />
                </div>
              )}

              {/* TAB: Events List */}
              {activeTab === 'list' && (
                <>
                  {/* AI Intelligence Summary */}
                  {sortedEvents.length > 0 && <div className="dr-summary-block">
                    <div className="dr-summary-header">
                      <span className="dr-summary-label">█ Intelligence Assessment</span>
                      
                    </div>
                    {summaryLoading ? (
                      <div className="dr-summary-loading"><div className="forte-spin" style={{width:'24px',height:'24px',margin:'0 auto 8px'}}></div><div>Gathering risk assessment</div></div>
                    ) : summary && summary.sitrep ? (
                      <>
                        <div className={`dr-summary-content ${summaryExpanded ? "expanded" : "collapsed"}`}>
                          {summary.sitrep.split('\n').map((line, i) => {
                            if (!line.trim()) return null
                            const clean = line.replace(/\*\*/g, '').replace(/^#+\s*/, '').replace(/\[\d+\]/g, '').replace(/\[\w+\]/g, '')
                            const isHeading = line.startsWith('**') || line.startsWith('#')
                            const isBullet = line.trim().startsWith('-') || line.trim().startsWith('•')
                            const bulletText = isBullet ? clean.replace(/^\s*[-•]\s*/, '') : clean
                            if (isHeading) return <div key={i} className="dr-summary-heading">{clean}</div>
                            if (isBullet) return <div key={i} className="dr-summary-bullet"><span className="dr-bullet-mark">▪</span><span>{bulletText}</span></div>
                            return <p key={i}>{clean}</p>
                          })}
                        </div>
                        <div className="dr-summary-toggle" onClick={() => setSummaryExpanded(!summaryExpanded)}>
                          {summaryExpanded ? '▲ Show less' : 'Read full assessment ▼'}
                        </div>
                        {(() => {
                          const match = summary.forecast && summary.forecast.match(/Threat Score[:\s]*(\d+)\s*\/\s*10/i)
                          if (!match) return null
                          const score = parseInt(match[1])
                          const color = score >= 8 ? '#e55' : score >= 5 ? '#e88a2a' : '#C29717'
                          return (
                            <div className="dr-threat-score">
                              <span className="dr-threat-icon" style={{background: color}}>{score}</span>
                              <span className="dr-threat-label">Threat Score</span>
                              <span className="dr-threat-value" style={{color}}>{score}/10</span>
                              <span className="dr-threat-bar"><span style={{width: (score*10)+'%', background: color}}></span></span>
                            </div>
                          )
                        })()}
                      </>
                    ) : null}
                  </div>}

                  {/* Consolidated Map */}
                  {mapEvents.length > 0 && !loading && (
                    <div className="dr-fullmap" style={{marginBottom:'16px'}}>
                      <iframe title="events-map" width="100%" style={{ border: 0, borderRadius: '4px', height: '45vh' }}
                        srcDoc={mapSrcDoc} />
                      <div className="dr-map-legend">
                        <span><span className="dr-legend-dot" style={{background:'#C29717'}}></span> No casualties</span>
                        <span><span className="dr-legend-dot" style={{background:'#e88a2a'}}></span> 1-10 casualties</span>
                        <span><span className="dr-legend-dot" style={{background:'#e55'}}></span> 10+ casualties</span>
                        <span style={{color:'#666', marginLeft:'8px'}}>Circle size = severity</span>
                      </div>
                    </div>
                  )}

                  {loading ? (
                    <div className="dr-loading"><div className="forte-spin" style={{width:'32px',height:'32px',margin:'0 auto 8px'}}></div></div>
                  ) : sortedEvents.length === 0 ? (
                    <div className="dr-empty">No events found for the selected filters.</div>
                  ) : (
                    <div className="dr-list">
                      {sortedEvents.map((event) => {
                        const isExpanded = expandedId === event._id
                        const casualties = totalCasualties(event)
                        const hasLocation = (event.latitude && event.longitude) || (event.markers && event.markers.length > 0)
                        return (
                          <div key={event._id} className={`dr-event ${isExpanded ? 'expanded' : ''}`}>
                            <div className="dr-event-header" onClick={() => setExpandedId(isExpanded ? null : event._id)}>
                              <div className="dr-event-left">
                                <div className="dr-event-date">{event.startDate ? moment(event.startDate).format('DD MMM YYYY') : 'N/A'}{event.startDate && <span className="dr-event-ago"> — {timeAgo(event.startDate)}</span>}</div>
                                <div className="dr-event-title">{event.conflictName || 'Untitled Event'}</div>
                                <div className="dr-event-meta">
                                  <span className="dr-event-country">{event.country}</span>
                                  {event.typeOfViolence && <span className="dr-event-category">{VIOLENCE_ICONS[event.typeOfViolence] || ''} {event.typeOfViolence}</span>}
                                  {casualties > 0 && <span className="dr-event-casualties">{casualties} casualt{casualties !== 1 ? 'ies' : 'y'}</span>}
                                </div>
                                <div className="dr-event-desc-preview">{truncate(event.description || event.exactSource || '', 160)}</div>
                                {event.technicalReportUrl && <a href={event.technicalReportUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{display:'inline-flex',alignItems:'center',gap:'4px',fontSize:'10px',color:'#C29717',textDecoration:'none',marginTop:'4px',border:'1px solid rgba(194,151,23,0.25)',padding:'2px 8px',borderRadius:'2px'}}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#C29717" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>Technical report</a>}
                              </div>
                              <div className="dr-event-toggle"><span className={`dr-arrow ${isExpanded ? 'up' : ''}`}>&#9662;</span></div>
                            </div>
                            {isExpanded && (
                              <div className="dr-event-expanded">
                                <div className="dr-event-details">
                                  <div className="dr-detail-grid">
                                    <div className="dr-detail-item"><span className="dr-detail-label">Conflict</span><span className="dr-detail-value">{event.conflictName || '—'}</span></div>
                                    <div className="dr-detail-item"><span className="dr-detail-label">Country</span><span className="dr-detail-value">{event.country || '—'}</span></div>
                                    <div className="dr-detail-item"><span className="dr-detail-label">Violence Type</span><span className="dr-detail-value">{event.typeOfViolence ? (VIOLENCE_ICONS[event.typeOfViolence] || '') + ' ' + event.typeOfViolence : '—'}</span></div>
                                    <div className="dr-detail-item"><span className="dr-detail-label">Source</span><span className="dr-detail-value">{(event.typeOfSource === 'Manual Entry' ? 'Human Analyst' : event.typeOfSource === 'Data Crawling' ? 'Official statement' : event.typeOfSource) || '—'}</span></div>
                                    <div className="dr-detail-item"><span className="dr-detail-label">Start Date</span><span className="dr-detail-value">{event.startDate ? moment(event.startDate).format('DD MMM YYYY, HH:mm') : '—'}</span></div>
                                    <div className="dr-detail-item"><span className="dr-detail-label">End Date</span><span className="dr-detail-value">{event.endDate ? moment(event.endDate).format('DD MMM YYYY, HH:mm') : '—'}</span></div>
                                    {event.sideA && <div className="dr-detail-item"><span className="dr-detail-label">Side A</span><span className="dr-detail-value">{event.sideA}</span></div>}
                                    {event.sideB && <div className="dr-detail-item"><span className="dr-detail-label">Side B</span><span className="dr-detail-value">{event.sideB}</span></div>}
                                  </div>
                                  {casualties > 0 && (
                                    <div className="dr-casualties"><span className="dr-detail-label">Casualties Breakdown</span>
                                      <div className="dr-casualties-grid">
                                        {event.sideADeath > 0 && <div className="dr-cas-item"><span className="dr-cas-num">{event.sideADeath}</span><span className="dr-cas-label">Side A</span></div>}
                                        {event.sideBDeath > 0 && <div className="dr-cas-item"><span className="dr-cas-num">{event.sideBDeath}</span><span className="dr-cas-label">Side B</span></div>}
                                        {event.deathCivilians > 0 && <div className="dr-cas-item"><span className="dr-cas-num">{event.deathCivilians}</span><span className="dr-cas-label">Civilians</span></div>}
                                        {event.deathUnknown > 0 && <div className="dr-cas-item"><span className="dr-cas-num">{event.deathUnknown}</span><span className="dr-cas-label">Deaths</span></div>}
                                      </div>
                                    </div>
                                  )}
                                  {event.description && <div className="dr-full-desc"><span className="dr-detail-label">Description</span><p>{event.description}</p></div>}
                                  
                                  {event.images && event.images.length > 0 && (
                                    <div className="dr-images"><span className="dr-detail-label">Images</span>
                                      <div className="dr-images-grid">
                                        {event.images.map((img, i) => (
                                          <div key={i} className="dr-image-card" onClick={() => openLightbox(event.images, i)} style={{cursor:'pointer'}}>
                                            <img src={`${window.location.origin}${img.url}`} alt={img.originalName || 'event'} /><span className="dr-image-cat">{img.category}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {hasLocation && (
                                  <div className="dr-event-map">
                                    <iframe title={`map-${event._id}`} width="100%" height="100%" style={{ border: 0, borderRadius: '6px' }}
                                      srcDoc={buildEventMapSrc(event)} />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </>
              )}

              {/* TAB: Forecast */}
              {activeTab === 'forecast' && (
                <div className="dr-forecast">
                  {summaryLoading ? (
                    <div className="dr-loading"><div className="forte-spin" style={{width:'32px',height:'32px',margin:'0 auto 8px'}}></div><div>Gathering risk assessment</div></div>
                  ) : summary && summary.forecast ? (
                    <>
                      <div className="dr-summary-block">
                        <div className="dr-summary-header">
                          <span className="dr-summary-label">▲ Forecast</span>
                        </div>
                        <div className="dr-summary-content expanded">
                          {summary.forecast.split('\n').map((line, i) => {
                            if (!line.trim()) return null
                            const clean = line.replace(/\*\*/g, '').replace(/^#+\s*/, '').replace(/\[\d+\]/g, '').replace(/\[\w+\]/g, '')
                            const isHeading = line.startsWith('**') || line.startsWith('#')
                            const isBullet = line.trim().startsWith('-') || line.trim().startsWith('•')
                            const bulletText = isBullet ? clean.replace(/^\s*[-•]\s*/, '') : clean
                            if (isHeading) return <div key={i} className="dr-summary-heading">{clean}</div>
                            if (isBullet) return <div key={i} className="dr-summary-bullet"><span className="dr-bullet-mark">▪</span><span>{bulletText}</span></div>
                            return <p key={i}>{clean}</p>
                          })}
                          {(() => {
                            const match = summary.forecast.match(/Threat Score[:\s]*(\d+)\s*\/\s*10/i)
                            if (!match) return null
                            const score = parseInt(match[1])
                            const color = score >= 8 ? '#e55' : score >= 5 ? '#e88a2a' : '#C29717'
                            return (
                              <div className="dr-threat-score">
                                <span className="dr-threat-icon" style={{background: color}}>{score}</span>
                                <span className="dr-threat-label">Threat score</span>
                                <span className="dr-threat-value" style={{color}}>{score}/10</span>
                                <span className="dr-threat-bar"><span style={{width: (score*10)+'%', background: color}}></span></span>
                              </div>
                            )
                          })()}
                        </div>
                      </div>

                      {summary.threats && summary.threats.length > 0 && (
                        <>
                          <div className="dr-summary-block" style={{marginTop: '16px'}}>
                            <div className="dr-summary-header">
                              <span className="dr-summary-label">◎ Potential threat zones</span>
                            </div>
                            <div className="dr-threats-list">
                              {(() => {
                                const byCountry = {}
                                summary.threats.forEach(t => {
                                  const country = t.country || t.target || 'Region'
                                  if (!byCountry[country]) byCountry[country] = []
                                  byCountry[country].push(t)
                                })
                                return Object.entries(byCountry).map(([country, threats]) => (
                                  <div key={country} style={{marginBottom:'12px'}}>
                                    <div className="dr-threat-country-label">{country}</div>
                                    <div className="dr-threats-grid">
                                      {threats.map((t, i) => {
                                        const riskColor = t.risk === 'high' ? '#e55' : t.risk === 'medium' ? '#e88a2a' : '#C29717'
                                        return (
                                          <div key={i} className="dr-threat-item">
                                            <span className="dr-threat-risk-dot" style={{background: riskColor}}></span>
                                            <div>
                                              <div className="dr-threat-target">{t.target}</div>
                                              <div className="dr-threat-type">{t.type}, {t.risk} risk</div>
                                            </div>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                ))
                              })()}
                            </div>
                          </div>

                          <div className="dr-fullmap" style={{marginTop: '16px'}}>
                            <iframe title="threats-map" width="100%" style={{ border: 0, borderRadius: '4px', height: '60vh' }}
                              srcDoc={(() => {
                                const threats = summary.threats
                                const lats = threats.map(t => t.lat)
                                const lngs = threats.map(t => t.lng)
                                const bounds = `[[${Math.min(...lats)-0.3},${Math.min(...lngs)-0.3}],[${Math.max(...lats)+0.3},${Math.max(...lngs)+0.3}]]`
                                const circles = threats.map(t => {
                                  const color = t.risk === 'high' ? '#e55' : t.risk === 'medium' ? '#e88a2a' : '#C29717'
                                  return `L.circle([${t.lat},${t.lng}],{radius:${Math.min(Math.max(t.radius,3),6)*1000},color:"${color}",weight:1,fillColor:"${color}",fillOpacity:0.15,dashArray:"4 4",className:"threat-pulse"}).addTo(m).bindPopup("<b>${t.target.replace(/'/g,"&#39;")}</b><br>${t.type}<br>Risk: ${t.risk}<br>Radius: ${t.radius}km");L.circleMarker([${t.lat},${t.lng}],{radius:4,color:"${color}",weight:2,fillColor:"${color}",fillOpacity:0.9}).addTo(m);`
                                }).join('')
                                return '<!DOCTYPE html><html><head><link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/><script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script><style>html,body{margin:0;height:100%;width:100%}#m{width:100%;height:100%}.leaflet-tile{filter:brightness(1.4) contrast(1.3)}.leaflet-control-zoom a{background:#1a1a1a;color:#C29717;border-color:#333}.leaflet-control-zoom a:hover{background:#333}.leaflet-popup-content-wrapper{background:#1e1e1e;color:#ccc;border-radius:2px;border:1px solid rgba(255,255,255,0.1)}.leaflet-popup-tip{background:#1e1e1e}@keyframes threatPulse{0%{opacity:0.15;stroke-opacity:1}50%{opacity:0.05;stroke-opacity:0.4}100%{opacity:0.15;stroke-opacity:1}}.threat-pulse{animation:threatPulse 2.5s ease-in-out infinite}.leaflet-popup-content{font-size:12px;font-family:Inter,sans-serif}</style></head><body><div id="m"></div><script>var m=L.map("m",{zoomControl:true,attributionControl:false}).fitBounds(' + bounds + ');L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}").addTo(m);L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}").addTo(m);' + circles + '<\/script></body></html>'
                              })()} />
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="dr-empty">No forecast available. Select events to generate.</div>
                  )}
                </div>
              )}

              {/* TAB: Analytics */}
              {activeTab === 'analytics' && (
                <div className="dr-analytics">
                  <div className="dr-chart-block">
                    <div className="dr-chart-title">Daily Events &amp; Casualties</div>
                    <div className="dr-chart-subtitle">Incident frequency and severity over time</div>
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={350}>
                        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                          <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 10 }} tickFormatter={d => moment(d).format('DD MMM')} />
                          <YAxis yAxisId="left" tick={{ fill: '#666', fontSize: 10 }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fill: '#666', fontSize: 10 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend wrapperStyle={{ fontSize: '11px', color: '#888' }} />
                          <Bar yAxisId="left" dataKey="events" name="Events" fill="rgba(194,151,23,0.6)" radius={[2,2,0,0]} />
                          <Line yAxisId="right" type="monotone" dataKey="casualties" name="Casualties" stroke="#e55" strokeWidth={2} dot={{ r: 2, fill: '#e55' }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="dr-empty">No data for selected period.</div>
                    )}
                  </div>

                  <div className="dr-chart-block" style={{ marginTop: '20px' }}>
                    <div className="dr-chart-title">Cumulative Threat Level</div>
                    <div className="dr-chart-subtitle">Running total of casualties indicating escalation</div>
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={chartData.reduce((acc, d) => {
                          const prev = acc.length > 0 ? acc[acc.length - 1].cumCas : 0
                          acc.push({ ...d, cumCas: prev + d.casualties })
                          return acc
                        }, [])} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                          <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 10 }} tickFormatter={d => moment(d).format('DD MMM')} />
                          <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <defs>
                            <linearGradient id="threatGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#e55" stopOpacity={0.4} />
                              <stop offset="100%" stopColor="#e55" stopOpacity={0.05} />
                            </linearGradient>
                          </defs>
                          <Area type="monotone" dataKey="cumCas" name="Cumulative Casualties" stroke="#e55" fill="url(#threatGrad)" strokeWidth={1.5} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : null}
                  </div>

                  <div className="dr-chart-block" style={{ marginTop: '20px' }}>
                    <div className="dr-chart-title">Attack Types Over Time</div>
                    <div className="dr-chart-subtitle">Daily breakdown by violence category</div>
                    {(() => {
                      const typeMap = {}
                      const allTypes = new Set()
                      filteredEvents.forEach(e => {
                        if (!e.startDate || !e.typeOfViolence) return
                        const day = new Date(e.startDate).toISOString().split('T')[0]
                        if (!typeMap[day]) typeMap[day] = { date: day }
                        typeMap[day][e.typeOfViolence] = (typeMap[day][e.typeOfViolence] || 0) + 1
                        allTypes.add(e.typeOfViolence)
                      })
                      const typeData = Object.values(typeMap).sort((a, b) => a.date.localeCompare(b.date))
                      const typeColors = ['#C29717', '#e55', '#e88a2a', '#7a9ec2', '#9b7', '#c7a', '#8ad', '#db8', '#a8c', '#7bc']
                      const topTypes = [...allTypes].slice(0, 8)
                      return typeData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={typeData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                            <XAxis dataKey="date" tick={{ fill: '#999', fontSize: 10 }} tickFormatter={d => moment(d).format('DD MMM')} />
                            <YAxis tick={{ fill: '#999', fontSize: 10 }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend wrapperStyle={{ fontSize: '10px', color: '#888' }} />
                            {topTypes.map((t, i) => (
                              <Bar key={t} dataKey={t} stackId="a" fill={typeColors[i % typeColors.length]} radius={i === topTypes.length - 1 ? [2,2,0,0] : [0,0,0,0]} />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <div className="dr-empty">No data</div>
                    })()}
                  </div>
                </div>
              )}

              {/* TAB: Arsenal */}
              {activeTab === 'arsenal' && (
                <div className="dr-arsenal">
                  {arsenalLoading ? (
                    <div className="dr-loading"><div className="forte-spin" style={{width:'32px',height:'32px',margin:'0 auto 8px'}}></div></div>
                  ) : arsenalData && arsenalData.weapons && arsenalData.weapons.length > 0 ? (
                    <>
                      {['offensive','defensive'].map(role => {
                        const items = arsenalData.weapons.filter(w => w.role === role)
                        if (items.length === 0) return null
                        return (
                          <div key={role} className="dr-arsenal-section">
                            <div className="dr-arsenal-label">{role} systems</div>
                            <div className="dr-arsenal-grid">
                              {items.map((w, i) => (
                                <div key={i} className="dr-arsenal-card">
                                  {w.image ? <img src={window.location.origin + w.image} alt="" className="dr-arsenal-card-img" /> : <div className="dr-arsenal-card-noimg" />}
                                  <div className="dr-arsenal-card-body">
                                    <div className="dr-arsenal-name">{w.name}</div>
                                    {w.designation && <div className="dr-arsenal-desig">{w.designation}</div>}
                                    <div className="dr-arsenal-meta">{w.manufacturer}</div>
                                    <div className="dr-arsenal-meta">{w.operator} — {w.type}</div>
                                    <div className="dr-arsenal-meta">{w.range_km}km — {w.warhead}</div>
                                    <div className="dr-arsenal-freq">x{w.frequency}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })}

                      <div className="dr-arsenal-section">
                        <div className="dr-arsenal-label">Weapons deployment timeline</div>
                        <div className="dr-arsenal-timeline-desc">How weapon systems are being deployed over time — shifts indicate escalation or capability evolution</div>
                        {(() => {
                          const weaponsByDay = {}
                          const weaponNames = new Set()
                          filteredEvents.forEach(e => {
                            if (!e.startDate || !e.typeOfViolence) return
                            const day = new Date(e.startDate).toISOString().split('T')[0]
                            if (!weaponsByDay[day]) weaponsByDay[day] = {}
                            const vt = e.typeOfViolence
                            weaponsByDay[day][vt] = (weaponsByDay[day][vt] || 0) + 1
                            weaponNames.add(vt)
                          })
                          const days = Object.keys(weaponsByDay).sort()
                          const topWeapons = [...weaponNames].slice(0, 6)
                          const colors = ['#C29717','#e55','#e88a2a','#7a9ec2','#9b7','#c7a']
                          const data = days.map(d => ({ date: d, ...weaponsByDay[d] }))
                          return data.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                              <BarChart data={data} margin={{top:10,right:10,left:0,bottom:0}}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                                <XAxis dataKey="date" tick={{fill:'#888',fontSize:9}} tickFormatter={d => d.slice(5)} />
                                <YAxis tick={{fill:'#888',fontSize:10}} />
                                <Tooltip contentStyle={{background:'#1e1e1e',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'2px',fontSize:'11px',color:'#ccc'}} />
                                {topWeapons.map((w, i) => <Bar key={w} dataKey={w} stackId="a" fill={colors[i % colors.length]} radius={i === topWeapons.length - 1 ? [2,2,0,0] : [0,0,0,0]} />)}
                              </BarChart>
                            </ResponsiveContainer>
                          ) : null
                        })()}
                      </div>

                      <div className="dr-arsenal-section">
                        <div className="dr-arsenal-label">Escalation indicators</div>
                        <div className="dr-arsenal-timeline-desc">Weapon sophistication over time — progression from drones to ballistic missiles signals escalation ladder advancement</div>
                        {(() => {
                          const sophistication = { 'Drone Attack': 1, 'Armed Assault': 1, 'IED / Roadside Bomb': 1, 'Ambush': 1, 'Riot / Civil Unrest': 1, 'Sabotage': 2, 'Shelling / Artillery': 2, 'Car Bombing (VBIED)': 2, 'Bombing': 3, 'Suicide Attack': 3, 'Maritime Attack': 3, 'Airstrike': 4, 'Missile Attack': 5, 'Chemical / Biological Attack': 6 }
                          const dayData = {}
                          filteredEvents.forEach(e => {
                            if (!e.startDate) return
                            const day = new Date(e.startDate).toISOString().split('T')[0]
                            const score = sophistication[e.typeOfViolence] || 2
                            if (!dayData[day]) dayData[day] = { maxScore: 0, total: 0, count: 0 }
                            dayData[day].maxScore = Math.max(dayData[day].maxScore, score)
                            dayData[day].total += score
                            dayData[day].count += 1
                          })
                          const data = Object.entries(dayData).sort((a,b) => a[0].localeCompare(b[0])).map(([d, v]) => ({
                            date: d, peak: v.maxScore, avg: Math.round(v.total / v.count * 10) / 10
                          }))
                          return data.length > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                              <ComposedChart data={data} margin={{top:10,right:10,left:0,bottom:0}}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                                <XAxis dataKey="date" tick={{fill:'#888',fontSize:9}} tickFormatter={d => d.slice(5)} />
                                <YAxis tick={{fill:'#888',fontSize:10}} domain={[0,6]} ticks={[1,2,3,4,5,6]} tickFormatter={v => ['','Low','Med','High','Air','Missile','WMD'][v] || ''} />
                                <Tooltip contentStyle={{background:'#1e1e1e',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'2px',fontSize:'11px',color:'#ccc'}} />
                                <Area type="monotone" dataKey="avg" name="Avg sophistication" fill="rgba(194,151,23,0.1)" stroke="#C29717" strokeWidth={1} />
                                <Line type="stepAfter" dataKey="peak" name="Peak threat" stroke="#e55" strokeWidth={2} dot={{r:2,fill:'#e55'}} />
                              </ComposedChart>
                            </ResponsiveContainer>
                          ) : null
                        })()}
                      </div>

                      <div className="dr-arsenal-section">
                        <div className="dr-arsenal-label" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                          <span>Individual weapons usage over time</span>
                          <span style={{display:'flex',gap:'4px'}}>
                            {['all','offensive','defensive'].map(f => (
                              <span key={f} onClick={() => setArsenalFilter(f)} style={{padding:'2px 8px',fontSize:'9px',cursor:'pointer',borderRadius:'2px',textTransform:'capitalize',
                                background: arsenalFilter === f ? 'rgba(194,151,23,0.15)' : 'transparent',
                                border: arsenalFilter === f ? '1px solid rgba(194,151,23,0.3)' : '1px solid rgba(255,255,255,0.08)',
                                color: arsenalFilter === f ? '#C29717' : '#888'
                              }}>{f}</span>
                            ))}
                          </span>
                        </div>
                        <div className="dr-arsenal-timeline-desc">Tracking deployment frequency of specific weapon systems — surges indicate operational tempo shifts, new entries signal capability introduction</div>
                        {(() => {
                          const weapons = arsenalData.weapons.filter(w => w.frequency >= 1 && (arsenalFilter === 'all' || w.role === arsenalFilter)).sort((a,b) => b.frequency - a.frequency).slice(0, 10)
                          const weaponTypeMap = {}
                          weapons.forEach(w => {
                            const types = []
                            if (w.name.toLowerCase().includes('shahed') || w.name.toLowerCase().includes('mohajer') || w.name.toLowerCase().includes('ababil') || w.name.toLowerCase().includes('arash') || w.type === 'drone') types.push('Drone Attack')
                            if (w.name.toLowerCase().includes('fateh') || w.name.toLowerCase().includes('qiam') || w.name.toLowerCase().includes('emad') || w.name.toLowerCase().includes('sejjil') || w.name.toLowerCase().includes('zolfaghar') || w.name.toLowerCase().includes('khorramshahr') || w.type === 'missile') types.push('Missile Attack')
                            if (w.type === 'air_defense') types.push('Missile Attack')
                            if (w.type === 'naval') types.push('Maritime Attack')
                            if (w.type === 'rocket' || w.type === 'artillery') types.push('Shelling / Artillery')
                            if (types.length === 0) types.push(w.type)
                            weaponTypeMap[w.name] = types
                          })

                          const dayMap = {}
                          filteredEvents.forEach(e => {
                            if (!e.startDate || !e.typeOfViolence) return
                            const day = new Date(e.startDate).toISOString().split('T')[0]
                            if (!dayMap[day]) dayMap[day] = {}
                            weapons.forEach(w => {
                              const types = weaponTypeMap[w.name] || []
                              if (types.includes(e.typeOfViolence)) {
                                dayMap[day][w.name] = (dayMap[day][w.name] || 0) + 1
                              }
                            })
                          })

                          const days = Object.keys(dayMap).sort()
                          const data = days.map(d => ({ date: d, ...dayMap[d] }))
                          const lineColors = ['#C29717','#e55','#e88a2a','#7a9ec2','#9b7','#c7a','#8ad','#db8','#a8c','#7bc']

                          return data.length > 0 ? (
                            <>
                              <ResponsiveContainer width="100%" height={320}>
                                <LineChart data={data} margin={{top:10,right:10,left:0,bottom:0}}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                                  <XAxis dataKey="date" tick={{fill:'#888',fontSize:9}} tickFormatter={d => d.slice(5)} />
                                  <YAxis tick={{fill:'#888',fontSize:10}} />
                                  <Tooltip contentStyle={{background:'#1e1e1e',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'2px',fontSize:'11px',color:'#ccc'}} />
                                  {weapons.map((w, i) => (
                                    <Line key={w.name} type="monotone" dataKey={w.name} stroke={lineColors[i % lineColors.length]} strokeWidth={w.frequency > 5 ? 2.5 : 1.5} dot={{r: w.frequency > 5 ? 3 : 1.5, fill: lineColors[i % lineColors.length]}} connectNulls={false} />
                                  ))}
                                </LineChart>
                              </ResponsiveContainer>
                              <div className="dr-arsenal-legend">
                                {weapons.map((w, i) => (
                                  <span key={w.name} className="dr-arsenal-legend-item">
                                    <span style={{background: lineColors[i % lineColors.length]}} className="dr-arsenal-legend-dot"></span>
                                    {w.name} <span className="dr-arsenal-legend-count">({w.frequency})</span>
                                  </span>
                                ))}
                              </div>
                            </>
                          ) : null
                        })()}
                      </div>
                    </>
                  ) : (
                    <div className="dr-empty">No arsenal data. Analysis will generate on first load.</div>
                  )}
                </div>
              )}

              {/* TAB: Airspace */}
              {activeTab === 'airspace' && (
                <div className="dr-airspace">
                  {airspaceLoading ? (
                    <div className="dr-loading"><div className="forte-spin" style={{width:'32px',height:'32px',margin:'0 auto 8px'}}></div><div>Checking airspace status</div></div>
                  ) : airspaceData && airspaceData.airports && airspaceData.airports.length > 0 ? (
                    <>
                      <div className="dr-airspace-updated">Last update: {airspaceData.updatedAt ? new Date(airspaceData.updatedAt).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}) + ' UTC' : 'N/A'} {airspaceData.cached ? '(cached)' : ''}</div>
                      {(() => {
                        const byCountry = {}
                        airspaceData.airports.forEach(a => {
                          const co = a.country || 'Other'
                          if (!byCountry[co]) byCountry[co] = []
                          byCountry[co].push(a)
                        })
                        return Object.entries(byCountry).map(([country, airports]) => (
                          <div key={country} className="dr-airspace-country">
                            <div className="dr-airspace-country-label">{country}</div>
                            <div className="dr-airspace-list">
                              {airports.map((a, i) => {
                                const color = a.status === 'open' ? '#4CAF50' : a.status === 'restricted' ? '#e88a2a' : '#e55'
                                return (
                                  <div key={i} className="dr-airspace-item">
                                    <span className="dr-airspace-dot" style={{background: color}}></span>
                                    <div className="dr-airspace-info">
                                      <div className="dr-airspace-name">{a.iata} - {a.name}</div>
                                      <div className="dr-airspace-summary">{a.summary}</div>
                                    </div>
                                    <span className="dr-airspace-status" style={{color}}>{a.status}</span>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        ))
                      })()}

                      <div className="dr-airspace-map">
                        <iframe title="airspace-map" width="100%" style={{border:0,borderRadius:'4px',height:'350px'}}
                          srcDoc={(() => {
                            const aps = airspaceData.airports.filter(a => a.lat && a.lng)
                            if (aps.length === 0) return ''
                            const lats = aps.map(a => a.lat), lngs = aps.map(a => a.lng)
                            const bounds = '[[' + (Math.min(...lats)-1) + ',' + (Math.min(...lngs)-1) + '],[' + (Math.max(...lats)+1) + ',' + (Math.max(...lngs)+1) + ']]'
                            const markers = aps.map(a => {
                              const color = a.status === 'open' ? '#4CAF50' : a.status === 'restricted' ? '#e88a2a' : '#e55'
                              return "L.circleMarker([" + a.lat + "," + a.lng + "],{radius:8,color:'" + color + "',weight:2,fillColor:'" + color + "',fillOpacity:0.8}).addTo(m).bindPopup('<b>" + a.iata + "</b><br>" + (a.name||'').replace(/'/g,'') + "<br><b>" + a.status + "</b>');"
                            }).join('')
                            return '<!DOCTYPE html><html><head><link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/><script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script><style>html,body{margin:0;height:100%;width:100%}#m{width:100%;height:100%}.leaflet-tile{filter:brightness(1.4) contrast(1.3)}.leaflet-control-zoom a{background:#1a1a1a;color:#C29717;border-color:#333}.leaflet-popup-content-wrapper{background:#1e1e1e;color:#ccc;border-radius:2px;border:1px solid rgba(255,255,255,0.1)}.leaflet-popup-tip{background:#1e1e1e}.leaflet-popup-content{font-size:12px;font-family:Inter,sans-serif}</style></head><body><div id="m"></div><script>var m=L.map("m",{zoomControl:true,attributionControl:false}).fitBounds(' + bounds + ');L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}").addTo(m);L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}").addTo(m);' + markers + '<\/script></body></html>'
                          })()} />
                      </div>
                    </>
                  ) : (
                    <div className="dr-empty">No airspace data available.</div>
                  )}
                </div>
              )}

              {/* TAB: Reports */}
              {activeTab === 'reports' && (
                <div className="dr-reports-tab">
                  {/* Sub-tabs */}
                  <div style={{display:'flex',gap:'2px',marginBottom:'12px'}}>
                    {[{k:'all',l:'All',c:allReportsCount},{k:'intel',l:'Intelligence reports',c:matchingReports.length},{k:'technical',l:'Technical reports',c:technicalReports.length}].map(t => (
                      <button key={t.k} onClick={() => setReportSubTab(t.k)} style={{
                        padding:'5px 14px',fontSize:'10px',fontFamily:'Inter,sans-serif',
                        textTransform:'uppercase',letterSpacing:'0.08em',cursor:'pointer',
                        fontWeight:reportSubTab===t.k?500:400,
                        background:reportSubTab===t.k?'rgba(194,151,23,0.1)':'transparent',
                        color:reportSubTab===t.k?'#C29717':'#666',
                        border:reportSubTab===t.k?'1px solid rgba(194,151,23,0.25)':'1px solid rgba(255,255,255,0.04)',
                        borderRadius:'2px',
                      }}>{t.l} {t.c > 0 && <span style={{marginLeft:'4px',opacity:0.6}}>({t.c})</span>}</button>
                    ))}
                  </div>

                  {/* Technical reports */}
                  {(reportSubTab === 'all' || reportSubTab === 'technical') && technicalReports.length > 0 && (
                    technicalReports.map(tr => (
                      <div key={tr._id} className="dr-report" style={{marginBottom:'8px'}}>
                        <div className="dr-report-header" onClick={() => window.open(tr.url, '_blank')} style={{cursor:'pointer'}}>
                          <div>
                            <div className="dr-report-date">{tr.date ? new Date(tr.date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : ''}</div>
                            <div className="dr-report-cats"><span className="dr-report-cat-tag" style={{background:'rgba(194,151,23,0.08)',color:'#C29717',border:'1px solid rgba(194,151,23,0.2)'}}>Technical report</span></div>
                            <div className="dr-report-title">{tr.title}</div>
                            <div className="dr-report-countries">
                              {(tr.countries||[]).map(co => <span key={co} className="dr-report-country">{co}</span>)}
                            </div>
                            {tr.description && <div style={{fontSize:'11px',color:'#888',marginTop:'4px',lineHeight:'1.5'}}>{(tr.description||'').substring(0,120)}...</div>}
                          </div>
                          <div className="dr-report-actions">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C29717" strokeWidth="1.5" style={{opacity:0.7}}><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  {/* Intelligence reports */}
                  {(reportSubTab === 'all' || reportSubTab === 'intel') && (matchingReports.length === 0 && reportSubTab === 'intel' ? (
                    <div className="dr-empty">No intelligence reports for selected countries.</div>
                  ) : (
                    (trial ? matchingReports.slice(0, TRIAL_VISIBLE_REPORTS) : matchingReports).map((r, idx) => {
                      const isExp = expandedReport === r._id || (expandedReport === 'first' && idx === 0)
                      return (
                        <div key={r._id} className={`dr-report ${isExp ? 'expanded' : ''}`}>
                          <div className="dr-report-header" onClick={() => setExpandedReport(isExp ? null : r._id)}>
                            <div>
                              <div className="dr-report-date">{r.date ? new Date(r.date).toLocaleDateString("en-GB", {day:"numeric",month:"short",year:"numeric"}) : ''}</div>
                              <div className="dr-report-cats">{(r.categories || ["Intelligence"]).map(cat => <span key={cat} className="dr-report-cat-tag">{cat} report</span>)}</div>
                              <div className="dr-report-title">{r.title}</div>
                              <div className="dr-report-countries">
                                {(r.countries || []).map(co => <span key={co} className="dr-report-country">{co}</span>)}
                              </div>
                            </div>
                            <div className="dr-report-actions">
                              <span className="dr-report-dl" onClick={(e) => { e.stopPropagation(); window.open((process.env.REACT_APP_APIHOST || 'http://localhost:3033') + '/report-view/' + r._id, '_blank'); }} title="View full report">&#8681;</span>
                              <span className={`dr-arrow ${isExp ? 'up' : ''}`}>&#9662;</span>
                            </div>
                          </div>
                          {isExp && (
                            <div className="dr-report-body">
                              <div className="dr-report-content" dangerouslySetInnerHTML={{__html: formatContent(r.content)}} />
                              {r.images && r.images.length > 0 && (
                                <div className="dr-report-images">
                                  {r.images.map((img, i) => (
                                    <div key={i} className="dr-report-img" onClick={() => openLightbox(r.images, i)} style={{cursor:'pointer'}}>
                                      <img src={window.location.origin + img.url} alt={img.label || ''} />
                                      {img.label && <span className="dr-report-img-label">{img.label}</span>}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {r.markers && r.markers.length > 0 && (
                                <div className="dr-report-markers-map">
                                  <iframe title={"rmap-"+r._id} width="100%" style={{border:0,borderRadius:'2px',height:'250px'}}
                                    srcDoc={(() => {
                                      const mks = r.markers;
                                      const lats = mks.map(m => m.lat);
                                      const lngs = mks.map(m => m.lng);
                                      const bounds = "[[" + (Math.min(...lats)-0.3) + "," + (Math.min(...lngs)-0.3) + "],[" + (Math.max(...lats)+0.3) + "," + (Math.max(...lngs)+0.3) + "]]";
                                      const circles = mks.map(m => "L.circleMarker([" + m.lat + "," + m.lng + "],{radius:5,color:'#e55',weight:2,fillColor:'#e55',fillOpacity:0.9}).addTo(map);L.circle([" + m.lat + "," + m.lng + "],{radius:3000,color:'#e55',weight:0.8,fillColor:'#e55',fillOpacity:0.06,dashArray:'3 3'}).addTo(map);").join("");
                                      
                                      return '<!DOCTYPE html><html><head><link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/><script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script><style>html,body{margin:0;height:100%}#m{height:100%}.leaflet-tile{filter:brightness(1.4) contrast(1.3)}.leaflet-control-zoom a{background:#1a1a1a;color:#C29717;border-color:#333}</style></head><body><div id="m"></div><script>var map=L.map("m",{zoomControl:false,attributionControl:false}).fitBounds(' + bounds + ');L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}").addTo(map);L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}").addTo(map);' + circles + '<\/script></body></html>';
                                    })()} />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })
                  ))}

                  {/* Trial: blurred preview of the remaining reports */}
                  {trial && (reportSubTab === 'all' || reportSubTab === 'intel') && matchingReports.length > TRIAL_VISIBLE_REPORTS && (
                    <div style={{ position: 'relative', marginTop: 8 }}>
                      <div style={{ filter: 'blur(5px)', WebkitFilter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none', maxHeight: 340, overflow: 'hidden' }}>
                        {matchingReports.slice(TRIAL_VISIBLE_REPORTS).map(r => (
                          <div key={r._id} className="dr-report" style={{ marginBottom: '8px' }}>
                            <div className="dr-report-header">
                              <div>
                                <div className="dr-report-date">{r.date ? new Date(r.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ''}</div>
                                <div className="dr-report-cats">{(r.categories || ["Intelligence"]).map(cat => <span key={cat} className="dr-report-cat-tag">{cat} report</span>)}</div>
                                <div className="dr-report-title">{r.title}</div>
                                <div className="dr-report-countries">{(r.countries || []).map(co => <span key={co} className="dr-report-country">{co}</span>)}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <TrialCta title={(matchingReports.length - TRIAL_VISIBLE_REPORTS) + " more reports in your regions"} sub="Your trial previews the latest intelligence reports. Upgrade to read the full archive." />
                    </div>
                  )}

                  {/* Empty state for all tab */}
                  {reportSubTab === 'all' && allReportsCount === 0 && (
                    <div className="dr-empty">No reports for selected countries.</div>
                  )}
                </div>
              )}
              </div>{/* end trial-gated panels wrapper */}


            </div>
          </div>
        </section>

        {lightbox.open && (
          <div className="dr-lightbox-overlay" onClick={closeLightbox}>
            <div className="dr-lightbox" onClick={(e) => e.stopPropagation()}>
              <button className="dr-lb-close" onClick={closeLightbox}>&times;</button>
              {lightbox.images.length > 1 && <button className="dr-lb-arrow dr-lb-prev" onClick={prevImage}>&#8249;</button>}
              <div className="dr-lb-img-wrap"><img src={`${window.location.origin}${lightbox.images[lightbox.index]?.url}`} alt="" /></div>
              {lightbox.images.length > 1 && <button className="dr-lb-arrow dr-lb-next" onClick={nextImage}>&#8250;</button>}
              <div className="dr-lb-info">
                <span className="dr-lb-cat">{lightbox.images[lightbox.index]?.label || lightbox.images[lightbox.index]?.category}</span>
                <span className="dr-lb-counter">{lightbox.index + 1} / {lightbox.images.length}</span>
              </div>
            </div>
          </div>
        )}

        {!embedded && <footer className="footer sm">
          <div className="auto__container">
            <div className="footer__inner">
              <div className="footer__inner-copy">
                <div className="footer__inner-side"><div className="footer__inner-logo"><img src="icons/logo.webp" alt="logo" /></div><p>FORTE INTEL | 2026</p></div>
                <div className="footer__inner-links">
                  <a href="/#mission">MISSION</a><a href="/#services">SERVICES</a><a href="/#coverage">COVERAGE</a><a href="/#partners">PARTNERS</a><a href="/#contacts">CONTACTS</a>
                  <Link to="/privacy" className="footer__inner-log">PRIVACY</Link>
                </div>
                <Link to="/login" className="footer__inner-log">LOGIN</Link>
              </div>
            </div>
          </div>
        </footer>}
      </main>
    </>
  )
}

export { DailyReports }
export default withRouter(DailyReports)
